'use client'

import { ReactNode } from 'react'

import { useFieldContext } from '@ui/components/forms/formContext'
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@ui/components/shadcn/ui/field'
import { cn } from '@ui/lib/utils'

export type BaseFieldProps = {
  label: ReactNode
  // Keeps the label for assistive tech (sr-only) when the control's
  // placeholder carries it visually.
  hideLabel?: boolean
  description?: ReactNode
  required?: boolean
  disabled?: boolean
  className?: string
}

export type ControlProps = {
  id: string
  'aria-invalid': boolean
  'aria-required': true | undefined
  'aria-describedby': string | undefined
  disabled: boolean | undefined
}

export type FieldShellRenderArgs<TValue> = {
  field: ReturnType<typeof useFieldContext<TValue>>
  showError: boolean
  controlProps: ControlProps
}

export function FieldShell<TValue>({
  label,
  hideLabel,
  description,
  required,
  disabled,
  orientation = 'vertical',
  className,
  labelClassName,
  children,
}: BaseFieldProps & {
  orientation?: 'vertical' | 'horizontal'
  labelClassName?: string
  children: (args: FieldShellRenderArgs<TValue>) => ReactNode
}) {
  const field = useFieldContext<TValue>()
  const showError = field.state.meta.isTouched && !field.state.meta.isValid
  const id = field.name
  const descriptionId = `${id}-description`
  const errorId = `${id}-error`

  const controlProps: ControlProps = {
    id,
    'aria-invalid': showError,
    'aria-required': required ? true : undefined,
    'aria-describedby': showError
      ? errorId
      : description
        ? descriptionId
        : undefined,
    disabled,
  }

  const labelNode = (
    <FieldLabel
      htmlFor={id}
      className={cn(
        orientation === 'horizontal' && 'font-normal',
        hideLabel && 'sr-only',
        labelClassName,
      )}
    >
      {label}
      {required && <span aria-hidden>*</span>}
    </FieldLabel>
  )

  // Error replaces description so field height holds steady. Render both to
  // match shadcn's default.
  const helperNode = showError ? (
    <FieldError id={errorId} errors={field.state.meta.errors} />
  ) : description ? (
    <FieldDescription id={descriptionId}>{description}</FieldDescription>
  ) : null

  const control = children({ field, showError, controlProps })

  if (orientation === 'horizontal') {
    // A bare label centers the control; a FieldContent stack top-anchors it.
    return (
      <Field
        orientation="horizontal"
        data-invalid={showError}
        className={className}
      >
        {control}
        {helperNode ? (
          <FieldContent>
            {labelNode}
            {helperNode}
          </FieldContent>
        ) : (
          labelNode
        )}
      </Field>
    )
  }

  // The helper line is always reserved so an arriving error never shifts the
  // fields below it.
  return (
    <Field data-invalid={showError} className={className}>
      {labelNode}
      {control}
      <div className="min-h-5">{helperNode}</div>
    </Field>
  )
}
